/**
 * Settings: language, sounds, how to play, and sending feedback. Reachable from every
 * screen through the ⚙ button. scope.md §8.1 and §10.
 */

import { useState } from 'react';
import { LOCALES, type Locale } from '@garbage/shared';
import { useGame } from '../store.js';
import { useT } from '../i18n.js';
import { FeedbackForm } from './FeedbackForm.js';
import { PackPicker } from './PackPicker.js';

export function SettingsButton({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      data-testid="settings-button"
      onClick={onClick}
      aria-label={t('common.settings')}
      className="rounded-full bg-white/10 px-3 text-lg font-black"
    >
      ⚙
    </button>
  );
}

export function SettingsSheet({
  onClose, onHelp, screen, startWithFeedback = false,
}: {
  onClose: () => void;
  /** Offered when there are rules worth reading from where the player is. */
  onHelp?: () => void;
  /** Which screen it was opened from, sent with any feedback. */
  screen: string;
  startWithFeedback?: boolean;
}) {
  const t = useT();
  const locale = useGame((s) => s.locale);
  const sound = useGame((s) => s.sound);
  const setLocale = useGame((s) => s.setLocale);
  const setSound = useGame((s) => s.setSound);
  const pack = useGame((s) => s.pack);
  const setPack = useGame((s) => s.setPack);
  const [feedback, setFeedback] = useState(startWithFeedback);

  return (
    <div
      role="dialog"
      aria-label={feedback ? t('feedback.title') : t('settings.title')}
      data-testid="settings-sheet"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-felt p-5 pb-[calc(1.25rem+var(--pad-bottom))]"
        onClick={(event) => event.stopPropagation()}
      >
        {feedback ? (
          <FeedbackForm screen={screen} locale={locale} onDone={onClose} onBack={() => setFeedback(false)} />
        ) : (
          <>
            <h2 className="mb-4 text-center text-xl font-black text-sun">{t('settings.title')}</h2>

            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-bone/50">{t('settings.language')}</p>
            <div className="mb-4 grid grid-cols-2 gap-2">
              {LOCALES.map((option) => (
                <Toggle
                  key={option.id}
                  testId={`locale-${option.id}`}
                  pressed={locale === option.id}
                  onClick={() => setLocale(option.id as Locale)}
                  lang={option.id}
                >
                  {option.name}
                </Toggle>
              ))}
            </div>

            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-bone/50">{t('settings.sounds')}</p>
            <div className="mb-1 grid grid-cols-2 gap-2">
              <Toggle testId="sound-off" pressed={!sound} onClick={() => setSound(false)}>🔇 {t('settings.off')}</Toggle>
              <Toggle testId="sound-on" pressed={sound} onClick={() => setSound(true)}>🔊 {t('settings.on')}</Toggle>
            </div>
            <p className="mb-4 text-xs text-bone/50">{t('settings.soundsHint')}</p>

            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-bone/50">{t('settings.cardDesign')}</p>
            <div className="mb-4">
              <PackPicker pack={pack} onPack={setPack} />
            </div>

            {onHelp && (
              <button
                type="button"
                data-testid="settings-help"
                onClick={() => { onClose(); onHelp(); }}
                className="mb-2 w-full rounded-2xl bg-white/10 py-3 font-bold"
              >
                ? {t('common.howToPlay')}
              </button>
            )}
            <button
              type="button"
              data-testid="settings-feedback"
              onClick={() => setFeedback(true)}
              className="mb-3 w-full rounded-2xl bg-white/10 py-3 font-bold"
            >
              ✉ {t('settings.feedback')}
            </button>

            <p className="mb-3 text-center text-[11px] text-bone/40">{t('settings.records')}</p>

            <button
              type="button"
              data-testid="settings-close"
              onClick={onClose}
              className="w-full rounded-2xl bg-sun py-3 font-black text-ink"
            >
              {t('common.done')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Toggle({
  pressed, onClick, children, testId, lang,
}: {
  pressed: boolean; onClick: () => void; children: React.ReactNode; testId: string; lang?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={pressed}
      onClick={onClick}
      lang={lang}
      className={[
        'rounded-xl py-3 font-bold',
        pressed ? 'bg-sun text-ink' : 'bg-white/10',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
